import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Container, Typography, Card, CardContent, Button, CircularProgress, Alert, Tabs, Tab, Chip } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import AthleteCard from '../../components/athletes/AthleteCard';
import DrillHistory from '../../components/athletes/DrillHistory';
import MeasurementHistory from '../../components/athletes/MeasurementHistory';
import GearWeightHistory from '../../components/athletes/GearWeightHistory';
import TeamMembershipSection from '../../components/athletes/TeamMembershipSection';
import FootballHistorySection from '../../components/athletes/FootballHistorySection';
import { useAuth } from '../../context/AuthContext';
import { getAthlete, listTestingSessions, fetchTestTypes, listMeasurements, listGearWeights } from '../../services/athleteService';
import { listAthleteTeamMemberships } from '../../services/rosterService';
import { listTeamStaff } from '../../services/teamService';
import { hasProgramManagementAuthorityForTeam } from '../../services/programService';
import { getAthleteCardPhoto, getAthleteCardPhotoUrl, uploadAthleteCardPhoto, removeAthleteCardPhoto } from '../../services/athletePhotoService';
import { resolveCurrentMembership } from '../../utils/footballHistory';
import { derivePersonalBests } from '../../utils/testStats';
import { formatHeight } from '../../utils/heightUtils';
import { computeDisplayWeight, formatDisplayWeight } from '../../utils/gearWeightUtils';

function age(dob) {
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
}

function formatDate(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString();
}

// County/state context, formatted for display — see 011. Never inferred
// from anything (ZIP included); just renders whatever's actually stored.
// county/school_district are the canonical names, joined server-side by
// getAthlete() through the county_id/school_district_id references —
// athletes itself only stores the ids, never a duplicated name.
function formatCountyState(athlete) {
  const countyName = athlete.county?.name;
  if (countyName && athlete.state_code) return `${countyName} County, ${athlete.state_code}`;
  if (countyName) return `${countyName} County`;
  if (athlete.state_code) return athlete.state_code;
  return '—';
}

function Row({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', pb: 1 }}>
      <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>{label}</Typography>
      <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
    </Box>
  );
}

function AthleteProfileInner() {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isAdmin } = useAuth();
  const [athlete, setAthlete] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [testTypes, setTestTypes] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [gearWeights, setGearWeights] = useState([]);
  // Phase C.8 — a SEPARATE, page-level fetch of the same
  // listAthleteTeamMemberships() TeamMembershipSection/
  // FootballHistorySection already call themselves. This IS a
  // duplicated network call, accepted deliberately (per this phase's
  // own scope decision) rather than refactoring those two working,
  // already-shipped components to accept memberships as a prop — that
  // consolidation is left for a later polish pass, not bundled into
  // this feature's risk surface.
  const [memberships, setMemberships] = useState([]);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  const [tab, setTab] = useState(location.state?.tab ?? 0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Test Result Correction/Invalidation (055) — whether THIS viewer has
  // Team testing WRITE authority (team_admin/head_coach of that exact
  // team, or Program management authority — never assistant_coach,
  // matching record_team_test_attempt()'s own write check, 049) for
  // each distinct team_id represented among this athlete's Testing
  // History. Testing History can show sessions from more than one Team
  // over an athlete's history, so this is a map, not a single boolean.
  // Computed here (not in DrillHistory, which stays a pure
  // props-in component) by reusing the exact same listTeamStaff()/
  // hasProgramManagementAuthorityForTeam() calls TeamTestingPage.js's
  // own canConductTesting already uses — no new RPC. isAdmin is handled
  // separately inside DrillHistory itself, so it's not folded in here.
  const [teamWriteAuthorityByTeamId, setTeamWriteAuthorityByTeamId] = useState({});

  const load = useCallback(async () => {
    const [a, s, t, m, g, mem] = await Promise.all([
      getAthlete(athleteId), listTestingSessions(athleteId), fetchTestTypes(), listMeasurements(athleteId), listGearWeights(athleteId),
      listAthleteTeamMemberships(athleteId),
    ]);
    setAthlete(a);
    setSessions(s);
    setTestTypes(t);
    setMeasurements(m);
    setGearWeights(g);
    setMemberships(mem);
  }, [athleteId]);

  useEffect(() => {
    load().catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [load]);

  // Test Result Correction/Invalidation (055) — re-derive team write
  // authority whenever the loaded sessions (and therefore the set of
  // distinct Teams represented) change. A failed lookup for any one
  // team_id degrades to "not authorized" for that team only — never
  // throws, never blocks the rest of the page — since this only governs
  // whether a button is shown, not the RPC's own actual authority.
  useEffect(() => {
    let cancelled = false;
    const teamIds = [...new Set(sessions.map(s => s.team_id).filter(Boolean))];
    if (teamIds.length === 0) {
      setTeamWriteAuthorityByTeamId({});
      return undefined;
    }
    (async () => {
      const entries = await Promise.all(teamIds.map(async (teamId) => {
        try {
          const [staff, programAuthority] = await Promise.all([
            listTeamStaff(teamId),
            hasProgramManagementAuthorityForTeam(teamId),
          ]);
          const myRoles = staff
            .filter(s => s.user_id === currentUser?.id && s.status === 'active')
            .map(s => s.role);
          const authorized = programAuthority || myRoles.includes('team_admin') || myRoles.includes('head_coach');
          return [teamId, authorized];
        } catch {
          return [teamId, false];
        }
      }));
      if (!cancelled) setTeamWriteAuthorityByTeamId(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [sessions, currentUser?.id]);

  const currentMembership = useMemo(() => resolveCurrentMembership(memberships), [memberships]);
  const personalBests = useMemo(() => derivePersonalBests(sessions, testTypes), [sessions, testTypes]);
  // Phase C.8 — the Athlete Card photo belongs to ATHLETE + SEASON
  // (product decision, 050's own header) — never inferred from
  // team_athletes directly. Only resolvable when the athlete's current
  // membership's Team actually has a real Division/Season assigned;
  // absent that, there is no season_id to associate a photo with yet,
  // and upload is correctly disabled rather than guessing one.
  const cardSeasonId = currentMembership?.team?.division?.season?.id || null;

  const refreshPhoto = useCallback(async (seasonId) => {
    const row = await getAthleteCardPhoto(athlete?.id, seasonId);
    setPhotoUrl(row ? await getAthleteCardPhotoUrl(row.storage_path) : null);
  }, [athlete?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!athlete?.id || !cardSeasonId) {
      setPhotoUrl(null);
      return undefined;
    }
    setPhotoLoading(true);
    refreshPhoto(cardSeasonId)
      .catch(e => { if (!cancelled) setUploadError(e.message); })
      .finally(() => { if (!cancelled) setPhotoLoading(false); });
    return () => { cancelled = true; };
  }, [athlete?.id, cardSeasonId, refreshPhoto]);

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file next time
    if (!file || !athlete?.id || !cardSeasonId) return;
    setUploadError(null);
    setUploading(true);
    try {
      await uploadAthleteCardPhoto({ athleteId: athlete.id, seasonId: cardSeasonId, file });
      await refreshPhoto(cardSeasonId);
    } catch (err) {
      setUploadError(err.message || 'Could not upload that photo.');
    } finally {
      setUploading(false);
    }
  };

  // Phase C.8 browser-acceptance follow-up — Remove Photo. A simple
  // confirm() guards against an accidental click; refreshPhoto() is
  // only called AFTER the RPC succeeds, so a failed removal leaves the
  // currently-displayed photo exactly as it was (never falsely cleared)
  // and the error is surfaced the same way an upload failure already is.
  const handleRemoveClick = async () => {
    if (!athlete?.id || !cardSeasonId) return;
    const confirmed = window.confirm(
      `Remove ${athlete.first_name}'s current Season photo from the Athlete Card? ` +
      'This does not delete the original photo file — you can add a new Season photo anytime.'
    );
    if (!confirmed) return;
    setUploadError(null);
    setRemoving(true);
    try {
      await removeAthleteCardPhoto({ athleteId: athlete.id, seasonId: cardSeasonId });
      await refreshPhoto(cardSeasonId);
    } catch (err) {
      setUploadError(err.message || 'Could not remove that photo.');
    } finally {
      setRemoving(false);
    }
  };

  const refetchSessions = useCallback(async () => {
    const s = await listTestingSessions(athleteId);
    setSessions(s);
  }, [athleteId]);

  // Measurements can change athlete.height_in/weight_lb (the server-side
  // cache), so refetch both together — not just the measurement rows.
  const refetchMeasurements = useCallback(async () => {
    const [a, m] = await Promise.all([getAthlete(athleteId), listMeasurements(athleteId)]);
    setAthlete(a);
    setMeasurements(m);
  }, [athleteId]);

  // Gear weight never touches the athlete's cached height/weight, so this
  // only ever needs to refetch its own list.
  const refetchGearWeights = useCallback(async () => {
    const g = await listGearWeights(athleteId);
    setGearWeights(g);
  }, [athleteId]);

  const athleteAge = useMemo(() => (athlete ? age(athlete.date_of_birth) : null), [athlete]);

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  if (!athlete) return null;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>{athlete.first_name} {athlete.last_name}</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={`Grade ${athlete.grade}`} sx={{ bgcolor: '#131318', color: '#9eb8d0' }} />
            <Chip size="small" label={`DOB ${formatDate(athlete.date_of_birth)}`} sx={{ bgcolor: '#131318', color: '#9eb8d0' }} />
          </Box>
        </Box>
        <Button
          variant="outlined" color="primary" size="small"
          onClick={() => navigate(`/athletes/${athleteId}/edit`)}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Edit Profile
        </Button>
      </Box>

      {/* Phase C.8 — the headline Athlete Card summary. Existing
          identity rows/tabs below are UNCHANGED — some of this
          information now appears in both places; that overlap is a
          deliberate, reported transitional choice, not an oversight. */}
      <Box sx={{ mt: 3 }}>
        <input
          ref={fileInputRef} type="file" accept="image/*" hidden
          onChange={handleFileChange} data-testid="athlete-card-photo-input"
        />
        <AthleteCard
          athlete={athlete}
          membership={currentMembership}
          personalBests={personalBests}
          photoUrl={photoUrl}
          photoLoading={photoLoading}
          canUpload={!!cardSeasonId}
          uploading={uploading}
          uploadError={uploadError}
          onUploadClick={handleUploadClick}
          canRemove={!!cardSeasonId}
          removing={removing}
          onRemoveClick={handleRemoveClick}
        />
      </Box>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mt: 3, mb: 3, borderBottom: '1px solid #1e2a3a' }}>
        <Tab label="Profile" />
        <Tab label="Testing History" />
      </Tabs>

      {tab === 0 && (
        <>
          <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Row label="Date of birth" value={formatDate(athlete.date_of_birth)} />
                <Row label="Age" value={athleteAge !== null ? athleteAge : '—'} />
                <Row label="Grade" value={athlete.grade} />
                <Row label="Height" value={athlete.height_in ? formatHeight(athlete.height_in) : '—'} />
                <Row label="Weight" value={formatDisplayWeight(computeDisplayWeight(measurements, gearWeights))} />
                <Row label="School District" value={athlete.school_district?.name || '—'} />
                <Row label="County" value={formatCountyState(athlete)} />
                <Row label="ZIP" value={athlete.zip_code || '—'} />
              </Box>
            </CardContent>
          </Card>

          <MeasurementHistory
            athlete={athlete}
            measurements={measurements}
            gearWeights={gearWeights}
            currentUserId={currentUser?.id}
            isAdmin={isAdmin}
            onChanged={refetchMeasurements}
          />

          <GearWeightHistory
            athlete={athlete}
            gearWeights={gearWeights}
            currentUserId={currentUser?.id}
            isAdmin={isAdmin}
            onChanged={refetchGearWeights}
          />

          <TeamMembershipSection athlete={athlete} />

          <FootballHistorySection athlete={athlete} />
        </>
      )}

      {tab === 1 && (
        testTypes.length === 0 ? null : (
          <DrillHistory
            athleteId={athleteId}
            sessions={sessions}
            testTypes={testTypes}
            currentUserId={currentUser?.id}
            isAdmin={isAdmin}
            teamWriteAuthorityByTeamId={teamWriteAuthorityByTeamId}
            onChanged={refetchSessions}
          />
        )
      )}
    </Container>
  );
}

export default function AthleteProfilePage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><AthleteProfileInner /></RequireAuth>
    </Box>
  );
}
